import { Bar } from "react-chartjs-2";
import { Chart as ChartJS } from "chart.js/auto";
import {
  CategoryScale,
  type ChartData,
  type ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type TooltipModel,
} from "chart.js";
import ReactDOM from "react-dom/client";
import { ChartTooltip } from "~/features/cms/homepagePersonalization/cpd/components/CPDTooltip";
import { formatCurrencyShort } from "~/common/utils/currencyFormatter";
import { useEffect, useRef } from "react";
import {
  commonChartStyleProps,
  createCaret,
  createTooltipElement,
  positionTooltip,
} from "~/features/cms/homepagePersonalization/cpd/CPDUtils";
import { C_CHART_6, C_CHART_7, C_CHART_8 } from "@schaeffler-commerce/design-system";
import { styled, ThemeProvider } from "@schaeffler-commerce/design-system";
import { theme } from "~/app/theme";
import { useTranslation } from "react-i18next";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export interface TooltipData {
  invoice?: (number | null)[];
  orderbook?: (number | null)[];
  target?: (number | null)[];
  gap?: (number | null)[];
}

interface LastFiveYearsPerfromanceGraphProps {
  chartData: {
    target: (number | null)[];
    invoice: (number | null)[];
    orderbook: (number | null)[];
    gap: (number | null)[];
  };
  currencyCode: string;
}

export default function LastFiveYearsPerfromanceGraph({ chartData, currencyCode }: LastFiveYearsPerfromanceGraphProps) {
  const { invoice, orderbook, target, gap } = chartData;
  const tooltipRootRef = useRef<ReactDOM.Root | null>(null);
  const tooltipElRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- MANAGED-DOM-REF: the refs hold a manually created tooltip element and root, and the external chart handler fills them after the first hover; the cleanup must read the values current at unmount time, so a mount-time copy stays null and removes nothing (SCHAEFF-41513)
      tooltipRootRef.current?.unmount();
      // eslint-disable-next-line react-hooks/exhaustive-deps -- MANAGED-DOM-REF: same reason as the root above
      tooltipElRef.current?.remove();
    };
  }, []);

  const data: ChartData<"bar"> = {
    labels: ["Q1", "Q2", "Q3", "Q4", [t("cpd.graph1.Full.label"), t("cpd.graph1.Year.label")]],
    datasets: [
      {
        label: "Invoice",
        data: invoice,
        ...commonChartStyleProps(C_CHART_6),
      },
      {
        label: "Orderbook",
        data: orderbook,
        ...commonChartStyleProps(C_CHART_7),
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
      },
      {
        label: "Gap",
        data: gap,
        ...commonChartStyleProps(C_CHART_8),
      },
      {
        label: "Total",
        data: target,
        hidden: true,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          callback: function (value: number | string) {
            return formatCurrencyShort(value, currencyCode, "end");
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) =>
          externalTooltipHandler(
            context,
            {
              invoice,
              orderbook,
              gap,
              target,
            },
            tooltipRootRef,
            tooltipElRef,
            currencyCode,
          ),
      },
    },
  };

  return (
    <ChartView>
      <Bar options={options} data={data} />
    </ChartView>
  );
}

const externalTooltipHandler = (
  context: { chart: ChartJS; tooltip: TooltipModel<"bar"> },
  chartData: TooltipData,
  tooltipRootRef: React.MutableRefObject<ReactDOM.Root | null>,
  tooltipElRef: React.RefObject<HTMLDivElement | null>,
  currencyCode: string,
) => {
  const { invoice = [], orderbook = [], target = [], gap = [] } = chartData;
  const { chart, tooltip } = context;

  const tooltipEl = createTooltipElement(tooltipElRef);

  if (!tooltipRootRef.current) {
    tooltipRootRef.current = ReactDOM.createRoot(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  const dataPoint = tooltip.dataPoints?.[0];
  if (!dataPoint) {
    return;
  }

  const idx = dataPoint.dataIndex;

  const tooltipData = {
    invoice: invoice[idx] ?? null,
    orderbook: orderbook[idx] ?? null,
    target: target[idx] ?? null,
    gap: gap[idx] ?? null,
  };

  tooltipRootRef.current.render(
    <ThemeProvider theme={theme}>
      <ChartTooltip data={tooltipData} currencyIso={currencyCode} />
    </ThemeProvider>,
  );

  requestAnimationFrame(() => {
    createCaret(tooltipEl);
    positionTooltip(tooltipEl, chart, dataPoint);
  });
};

const ChartView = styled("div")({
  height: "244px",
});
